import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { InboxMessage as InboxMessageType } from "@/types/types";
import createDOMPurify from "dompurify";
import FileAttachment from "./FileAttachment";
import { useCampaignStore, useCreatorsStore } from "@/stores";
import { useCreatorsTableContextOptional } from "@/components/creators-table/CreatorsTableContext";
import { cleanHtml, formatPlainText } from "@/utils/emailFormatters";

interface InboxMessageProps {
  message: InboxMessageType;
  creator_id: string;
}

export default function InboxMessage({ message, creator_id }: InboxMessageProps) {
  const DOMPurify = React.useMemo(() => createDOMPurify(window), []);
  const { creators } = useCreatorsStore();
  const storeOwner = useCampaignStore((s) => s.campaign_owner);
  // Inside a creators table the row's own campaign says who "we" are (the
  // org-wide Global CRM mixes campaigns); the campaign Inbox tab uses the store.
  const tableContext = useCreatorsTableContextOptional();

  const creator = creators.find((c) => c._id === creator_id);
  const campaign_owner = tableContext
    ? tableContext.campaignFor(creator)?.campaign_owner
    : storeOwner;
  const creatorEmail = creator?.email;
  const campaignOwnerEmail = campaign_owner?.email;

  const from = typeof message.from === "string" ? message.from : "";
  const isFromCampaignOwner =
    campaignOwnerEmail &&
    from.toLowerCase().includes(campaignOwnerEmail.toLowerCase());
  const isFromCreator =
    creatorEmail && from.toLowerCase().includes(creatorEmail.toLowerCase());

  const avatarSrc = isFromCampaignOwner
    ? campaign_owner?.avatar || ""
    : isFromCreator
      ? creator?.avatar || ""
      : "";

  return (
    <div className="flex flex-col overflow-hidden p-5 pb-10">
      <div className="flex flex-row items-start w-full">
        <Avatar className="size-10 mr-5">
          <AvatarFallback className="bg-primary/15 text-primary border border-primary/60">
            <span>{from.charAt(0).toUpperCase()}</span>
          </AvatarFallback>
          <AvatarImage src={avatarSrc} />
        </Avatar>
        <div className=" flex flex-wrap w-full justify-between">
          <div className="flex flex-col w-auto">
            <p className="font-medium">{message.from}</p>
            <p className="text-xs text-muted-foreground">
              <span>To:</span> <span>{message.to}</span>
            </p>
            {message.cc && (
              <p className="text-xs text-muted-foreground">
                <span>Cc:</span> <span>{message.cc}</span>
              </p>
            )}
          </div>
          <span className="text-xs text-foreground/70 text-nowrap mt-1">
            <span>Sent</span> <span>{message.sent_at}</span>
          </span>
        </div>
      </div>
      <div
        translate="yes"
        className="notranslate-exempt text-sm ml-15 mt-2 overflow-auto w-auto min-w-sm border-none shadow-none text-foreground/90 format-prose max-w-xl [&_a]:text-primary [&_a]:underline [&_a]:hover:decoration-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_li]:mb-1 [&_img]:max-w-full [&_img]:h-auto [&_blockquote]:border-l-2 [&_blockquote]:border-muted-foreground/30 [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(
            cleanHtml(formatPlainText(message.content)),
          ),
        }}
      />
      {message.attachments && message.attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 ml-15 my-5">
          {message.attachments.map((attachment, index) => (
            <FileAttachment
              key={index}
              attachment={attachment}
              messageId={message.gmail_message_id || message.id}
              campaignCreatorId={creator_id}
            />
          ))}
        </div>
      )}
    </div>
  );
}